import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonWithPopoverAttached } from '@/core/ButtonWithPopoverAttached.molecule';
import { resetItemFinder } from '@/itemFinder/itemFinder.actions';
import { Feature } from '@/licenseManagement/licenseManagement.constants';
import { useHasValidFeature } from '@/licenseManagement/licenseManagement.store';
import { setAutoFocusTableDefinitionTab } from '@/main/layout.actions';
import { isScalingTableEditorEnabled } from '@/services/systemConfiguration.utilities';
import { resetTableDefinition, setShowCreateTemplate } from '@/tableDefinitionEditor/tableDefinition.actions';
import {
  CONTROL_PANE_DISPLAY,
  EDITOR_TYPES,
  TABLE_DEFINITION_TEMPLATE_OPTIONS,
} from '@/tableDefinitionEditor/tableDefinition.constants';
import { editTableDefinition } from '@/tableDefinitionEditor/tableDefinition.utilities';
import {
  resetTableEditorStore,
  setControlPane,
  setSelectedTemplateOption,
} from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { setActiveTool } from '@/toolSelection/investigate.actions';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { CreateApplyInScalingTableModal } from '@/tools/models/CreateApplyInScalingTableModal.molecule';
import { resetTemplate } from '@/tools/models/template.actions';
import { TEMPLATE_TYPES } from '@/tools/models/template.types';
import { trackCreateButtonClicked } from '@/tools/models/templateTracking.utilities';
import { setDisplayTableDefinitionEditor } from '@/worksheet/worksheet.actions';
import { Button } from '@seeqdev/qomponents';
import type { SelectedSearchItem } from '@/search/search.types';

export const CreateModelButton = () => {
  const { t } = useTranslation();
  const hasScalingTables = useHasValidFeature(Feature.Scaling_Tables);

  const [isItemSearchModalOpen, setIsItemSearchModalOpen] = useState(false);
  const [selectedScalingTable, setSelectedScalingTable] = useState<SelectedSearchItem | undefined>(undefined);
  const editorType = EDITOR_TYPES.SCALING;

  const cancelScalingTableSelection = () => {
    setIsItemSearchModalOpen(false);
    setSelectedScalingTable(undefined);
  };

  const createFromItems = () => {
    trackButtonClick('Items');
    resetTemplate(TEMPLATE_TYPES.TOOL);
    setActiveTool(TREND_TOOLS.MODEL_CREATE);
  };

  const trackButtonClick = (fromSource: string) => {
    trackCreateButtonClicked(fromSource, 'Tool');
  };

  return (
    <>
      {isScalingTableEditorEnabled() && hasScalingTables ? (
        <ButtonWithPopoverAttached
          id="createModel"
          label="CREATE"
          testId="createModel"
          onClick={createFromItems}
          showPopover={true}
          popoverContent={
            <div className="flexRowContainer tw:gap-1 tw:p-2">
              <Button
                onClick={createFromItems}
                label={t('TEMPLATES.CREATE_FROM_ITEMS')}
                size="sm"
                variant="outline"
                tooltip={t('TEMPLATES.CREATE_FROM_ITEMS_TOOLTIP')}
                tooltipOptions={{ position: 'right', delay: 0 }}
                testId="createModelFromItemsButton"
                extraClassNames="cursorPointer pl20 pr20 width-maximum fs14 tw:border-0 textAlignLeft noActiveBg"
              />
              <Button
                onClick={() => {
                  trackButtonClick('Scaling Table');
                  setIsItemSearchModalOpen(true);
                }}
                label={t('TEMPLATES.CREATE_FROM_SCALING_TABLE')}
                size="sm"
                variant="outline"
                tooltip={t('TEMPLATES.CREATE_FROM_SCALING_TABLE_TOOLTIP')}
                tooltipOptions={{ position: 'right', delay: 0 }}
                testId="createModelFromScalingTableButton"
                extraClassNames="cursorPointer pl20 pr20 width-maximum fs14 tw:border-0 textAlignLeft noActiveBg"
              />
            </div>
          }
        />
      ) : (
        <Button
          label={t('CREATE')}
          testId="createModelButton"
          onClick={createFromItems}
          tooltip={t('TEMPLATES.CREATE_FROM_ITEMS_TOOLTIP')}
          tooltipOptions={{ position: 'right', delay: 0 }}
        />
      )}
      {isItemSearchModalOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <CreateApplyInScalingTableModal
            header="TEMPLATES.MODAL_HEADER"
            helpText="TEMPLATES.CREATE_MODAL_HELP_TEXT"
            onClose={cancelScalingTableSelection}
            onSubmit={() => {
              setIsItemSearchModalOpen(false);
              setDisplayTableDefinitionEditor(true);
              setAutoFocusTableDefinitionTab(true);
              if (selectedScalingTable) {
                editTableDefinition(selectedScalingTable, editorType).then(() => {
                  setShowCreateTemplate(editorType, true);
                  setControlPane(editorType, CONTROL_PANE_DISPLAY.TEMPLATE_CONTROL);
                  setSelectedTemplateOption(editorType, TABLE_DEFINITION_TEMPLATE_OPTIONS.CREATE_TEMPLATE);
                });
              }
              setSelectedScalingTable(undefined);
            }}
            onCreateNewItem={() => {
              setIsItemSearchModalOpen(false);
              resetItemFinder();
              resetTableDefinition(editorType);
              resetTableEditorStore(editorType);
              setDisplayTableDefinitionEditor(true);
              setAutoFocusTableDefinitionTab(true);
              setShowCreateTemplate(editorType, true);
              setControlPane(editorType, CONTROL_PANE_DISPLAY.TEMPLATE_CONTROL);
              setSelectedTemplateOption(editorType, TABLE_DEFINITION_TEMPLATE_OPTIONS.CREATE_TEMPLATE);
            }}
            onSelectItem={(item) => setSelectedScalingTable(item)}
            selectedItem={selectedScalingTable}
            iconPartialTooltipKey="TEMPLATES.TEMPLATE_CREATE"
          />
        </div>
      )}
    </>
  );
};
